import type { IAutoReviewMissingField } from '@user/store/draftLeads/typings'

import type { AdditionalMissingKey, MissingFieldGroup } from '../typings'

const ASSIGNMENT_KEYS = [
  'salesTeamId',
  'assigneeId',
  'salesFunnelId',
  'salesFunnelStageId',
]

const ADDITIONAL_KEYS: AdditionalMissingKey[] = [
  'divisionId',
  'altPhoneNumber',
  'address',
  'locationTypeId',
  'note',
  'targetPests',
  'campaignId',
  'leadSourceId',
  'referral',
]

/**
 * Turns the server's missing-field list into render groups, in display order: name, phone or
 * email, branch, one assignment group, built-in additional fields, custom fields, then any
 * unrecognised key as `unknown`. Every input field lands in exactly one group.
 */
export function groupMissingFields(
  missing: IAutoReviewMissingField[],
): MissingFieldGroup[] {
  const groups: MissingFieldGroup[] = []
  const find = (key: string) =>
    missing.find((f) => !f.isCustomField && f.fieldKey === key)

  const name = find('nameOrCompany')
  if (name) groups.push({ kind: 'nameOrCompany', field: name })

  const phone = find('phoneOrEmail')
  if (phone) groups.push({ kind: 'phoneOrEmail', field: phone })

  const branch = find('branchId')
  if (branch) groups.push({ kind: 'branchId', field: branch })

  const assignment = missing.filter(
    (f) => !f.isCustomField && ASSIGNMENT_KEYS.includes(f.fieldKey ?? ''),
  )
  if (assignment.length > 0)
    groups.push({ kind: 'assignment', fields: assignment })

  const known = new Set<IAutoReviewMissingField>()
  if (name) known.add(name)
  if (phone) known.add(phone)
  if (branch) known.add(branch)
  assignment.forEach((f) => known.add(f))

  ADDITIONAL_KEYS.forEach((key) => {
    const field = find(key)
    if (field) {
      groups.push({ kind: 'additional', key, field })
      known.add(field)
    }
  })

  missing.forEach((field) => {
    if (field.isCustomField && field.fieldId !== null) {
      groups.push({ kind: 'custom', fieldId: field.fieldId, field })
      known.add(field)
    }
  })

  missing.forEach((field) => {
    if (!known.has(field)) groups.push({ kind: 'unknown', field })
  })

  return groups
}
